import { Injectable } from '@angular/core';

declare global {
  interface Window {
    gm_authFailure?: () => void;
  }
}

@Injectable({
  providedIn: 'root',
})
export class GoogleMapsLoaderService {
  private loadPromise?: Promise<void>;

  load(apiKey: string): Promise<void> {
    if (!apiKey) {
      console.error('[GoogleMaps] Config endpoint returned an empty API key');
      return Promise.reject(new Error('Google Maps API key is empty'));
    }

    if (window.google?.maps) {
      console.info('[GoogleMaps] JavaScript API is already available');
      return Promise.resolve();
    }

    if (this.loadPromise) {
      console.info('[GoogleMaps] Reusing the in-flight script request');
      return this.loadPromise;
    }

    console.info('[GoogleMaps] Loading JavaScript API', { origin: window.location.origin });
    const previousAuthFailure = window.gm_authFailure;
    window.gm_authFailure = () => {
      console.error('[GoogleMaps] Google rejected the API key or this HTTP referrer');
      previousAuthFailure?.();
    };

    this.loadPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&loading=async`;
      script.onload = () => {
        if (window.google?.maps) {
          console.info('[GoogleMaps] JavaScript API script loaded');
        } else {
          console.error('[GoogleMaps] Script loaded but window.google.maps is unavailable');
        }
        resolve();
      };
      script.onerror = () => {
        this.loadPromise = undefined;
        console.error('[GoogleMaps] Failed to download the JavaScript API script');
        reject(new Error('Google Maps API failed to load'));
      };
      document.head.appendChild(script);
    });

    return this.loadPromise;
  }
}